import { Center, Text3D } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useAtomValue } from 'jotai'
import { trackEvent } from '../api'
import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { useParams } from 'wouter'
import { clientAtom, languageAtom, minigameHideAndSeekCatchAtom, minigameHideAndSeekShotAtom, minigameStateAtom, teamsAtom } from '../GlobalState'
import YutBonus from '../components/YutBonus'
import translations from '../dictionaries/translations'
import { getNameFont } from '../logicHelpers/helpers'
import Earth from '../meshes/Earth'
import Mars from '../meshes/Mars'
import Moon from '../meshes/Moon'
import Neptune from '../meshes/Neptune'
import Rocket from '../meshes/Rocket'
import Saturn from '../meshes/Saturn'
import Star from '../meshes/Stars/Star'
import UfoNew from '../meshes/UfoNew'
import useStarRoll from '../shader/starRoll/StarRoll'
import { getSocket } from '../socket'

const socket = getSocket()
const font = '/fonts/Luckiest Guy_Regular.json'
const TILE_SIZE = 4
const TILE_GAP = 0.18
const BOARD_COLUMNS = 3
const BOARD_ROWS = 4
const MOVE_DURATION = 0.22
const ROLL_STARS_PER_ELIMINATION = 5

function Label({ children, color = 'white', position, size = 0.4 }) {
  return <Text3D font={getNameFont(String(children), font)} size={size} height={0.01} position={position} rotation={[-Math.PI / 2, 0, 0]}>
    <meshStandardMaterial color={color} />
    {children}
  </Text3D>
}

function ActionButton({ children, position, onClick, scale = 1, boxDims=[1, 1, 1] }) {
  const [hovered, setHovered] = useState(false)
  return <group position={position} scale={scale}>
    <mesh><boxGeometry args={boxDims} /><meshStandardMaterial color={hovered ? 'limegreen' : 'yellow'} /></mesh>
    <mesh
      position={[0, 0.06, 0]}
      onPointerEnter={(event) => { event.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer' }}
      onPointerLeave={(event) => { event.stopPropagation(); setHovered(false); document.body.style.cursor = 'default' }}
      onPointerDown={(event) => { event.stopPropagation(); onClick() }}
    ><boxGeometry args={boxDims} /><meshBasicMaterial transparent opacity={0} /></mesh>
    <Center position={[0, 0.1, 0]}><Label color='black' size={0.34} position={[0, 0, 0]}>{children}</Label></Center>
  </group>
}

function ControlButton({ direction, position, onClick }) {
  const [hovered, setHovered] = useState(false)
  const rotations = {
    up: [-Math.PI / 2, 0, 0],
    down: [Math.PI / 2, 0, 0],
    left: [0, 0, Math.PI / 2],
    right: [0, 0, -Math.PI / 2]
  }
  return <group position={position}>
    <mesh><boxGeometry args={[1.5, 0.06, 1.5]} /><meshStandardMaterial color={hovered ? 'limegreen' : 'yellow'} /></mesh>
    <mesh
      position={[0, 0.05, 0]}
      onPointerEnter={(event) => { event.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer' }}
      onPointerLeave={(event) => { event.stopPropagation(); setHovered(false); document.body.style.cursor = 'default' }}
      onPointerDown={(event) => { event.stopPropagation(); onClick() }}
    ><boxGeometry args={[1.5, 0.12, 1.5]} /><meshBasicMaterial transparent opacity={0} /></mesh>
    <mesh position={[0, 0.13, 0]} rotation={rotations[direction]}><coneGeometry args={[0.28, 0.72, 3]} /><meshStandardMaterial color='black' /></mesh>
  </group>
}

function cellPosition(row, column) {
  return [(column - 1) * (TILE_SIZE + TILE_GAP), (row - 2) * (TILE_SIZE + TILE_GAP) + 1]
}

function formationOffset(slot, count) {
  const spacing = 0.72
  if (count === 2) return [slot === 0 ? -spacing / 2 : spacing / 2, 0]
  if (count === 3) return [[0, -spacing / 3], [-spacing / 2, spacing / 3], [spacing / 2, spacing / 3]][slot]
  if (count === 4) return [[-spacing / 2, -spacing / 2], [spacing / 2, -spacing / 2], [-spacing / 2, spacing / 2], [spacing / 2, spacing / 2]][slot]
  return [0, 0]
}

function Planet({ index, partiallyCleared, cleared, clickable, spawned, onTileClick }) {
  const [row, column] = [Math.floor(index / BOARD_COLUMNS), index % BOARD_COLUMNS]
  const [x, z] = cellPosition(row, column)
  const [hovered, setHovered] = useState(false)
  const celestialBody = useRef(null)
  const celestialBodies = [
    <Earth scale={0.88} showParticles={false} />,
    <Star scale={1.7} color='#facc15' />,
    <Saturn scale={1.2} />,
    <Star scale={1.7} color='#facc15' />,
    <Moon scale={0.8} shiny />,
    <Star scale={1.7} color='#facc15' />,
    <Neptune scale={1.2} />,
    <Star scale={1.7} color='#facc15' />,
    <Mars scale={1.2} />,
    <Earth scale={0.88} showParticles={false} />,
    <Star scale={1.7} color='#facc15' />,
    <Saturn scale={1.2} />
  ]

  useFrame(() => {
    if (!celestialBody.current) return
    const target = !spawned || cleared ? 0 : partiallyCleared ? 0.5 : 1
    celestialBody.current.scale.x += (target - celestialBody.current.scale.x) * 0.18
    celestialBody.current.scale.y += (target - celestialBody.current.scale.y) * 0.18
    celestialBody.current.scale.z += (target - celestialBody.current.scale.z) * 0.18
  })

  useEffect(() => {
    if (hovered) document.body.style.cursor = clickable ? 'pointer' : 'default'
  }, [clickable, hovered])

  return <group
    position={[x, 0.3, z]}
    onPointerEnter={(event) => { event.stopPropagation(); setHovered(true) }}
    onPointerLeave={(event) => { event.stopPropagation(); setHovered(false); document.body.style.cursor = 'default' }}
    onPointerDown={(event) => { event.stopPropagation(); if (clickable) onTileClick(index) }}
  >
    <group ref={celestialBody}>{celestialBodies[index]}</group>
    <mesh position={[0, 0.15, 0]}>
      <boxGeometry args={[TILE_SIZE, 0.5, TILE_SIZE]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
    </mesh>
  </group>
}

function HiderToken({ player, isCurrentPlayer, formationSlot, formationCount, highHover, spawned }) {
  const token = useRef(null)
  const tween = useRef(null)
  const initialPosition = useRef(null)
  const previousTarget = useRef(null)
  const [removed, setRemoved] = useState(false)
  const hoverHeight = highHover ? 3.3 : 0
  const hiderZ = highHover ? 1 : 0
  const [cellX, cellZ] = cellPosition(player.row, player.column)
  const [offsetX, offsetZ] = formationOffset(formationSlot, formationCount)
  const targetX = cellX + offsetX
  const targetZ = cellZ + offsetZ + hiderZ
  const eliminated = Boolean(player.eliminated || player.eliminatedUntil)
  if (!initialPosition.current) initialPosition.current = [targetX, hoverHeight, targetZ]
  if (!previousTarget.current) previousTarget.current = { x: targetX, z: targetZ }
  useEffect(() => {
    const previous = previousTarget.current
    previousTarget.current = { x: targetX, z: targetZ }
    if (!token.current) return
    tween.current = {
      fromX: previous.x,
      fromZ: previous.z,
      toX: targetX,
      toZ: targetZ,
      elapsed: 0
    }
  }, [targetX, targetZ])

  useEffect(() => {
    if (!player.eliminated) {
      setRemoved(false)
      return undefined
    }
    const timeoutId = window.setTimeout(() => setRemoved(true), 280)
    return () => window.clearTimeout(timeoutId)
  }, [player.eliminated])

  useFrame((_, delta) => {
    if (!token.current) return
    if (tween.current) {
      tween.current.elapsed = Math.min(MOVE_DURATION, tween.current.elapsed + delta)
      const progress = 1 - Math.pow(1 - tween.current.elapsed / MOVE_DURATION, 3)
      token.current.position.x = THREE.MathUtils.lerp(tween.current.fromX, tween.current.toX, progress)
      token.current.position.z = THREE.MathUtils.lerp(tween.current.fromZ, tween.current.toZ, progress)
      if (progress === 1) tween.current = null
    }
    token.current.position.y = hoverHeight + Math.sin(performance.now() / 180 + player.column) * 0.06
    const targetScale = eliminated || !spawned ? 0 : 1
    token.current.scale.x += (targetScale - token.current.scale.x) * 0.24
    token.current.scale.y += (targetScale - token.current.scale.y) * 0.24
    token.current.scale.z += (targetScale - token.current.scale.z) * 0.24
  })

  if (removed) return null
  return <group ref={token} position={initialPosition.current}>
    {player.team === 0
      ? <Rocket position={[0, 0, -0.1]} rotation={[0, -Math.PI / 16, 0]} scale={1.3} onBoard />
      : <UfoNew position={[0, 0, 0.15]} rotation={[0, 0, 0]} scale={1.45} animate />}
    {highHover && <Center position={[0, 1.1, 0]}><Label color='darkgreen' size={0.34} position={[0, 0, -0.2]}>{player.name}</Label></Center>}
    {isCurrentPlayer && <pointLight color='#bef264' intensity={1.1} distance={2.4} />}
  </group>
}

function ResultTeamColumn({ team, players, teamName, winner, freeYutText }) {
  const color = team === 0 ? 'red' : 'turquoise'
  const x = team === 0 ? -4.5 : 2.3
  return <group position={[x, 0.25, -2.8]}>
    <Label color={color} size={0.5} position={[0, 0, 0]}>{teamName}</Label>
    {players.map((player, index) => <Label key={player.id} color={player.eliminated ? '#9ca3af' : 'white'} size={0.34} position={[0, 0, 0.8 + index * 0.65]}>{player.name}</Label>)}
    {winner && <YutBonus position={[0.6, -0.1, 1.4 + players.length * 0.65]} scale={0.7} alwaysShow enableClick={false} modelOnly />}
    {winner && <Center position={[0.9, 0, 2.65 + players.length * 0.65]}><Label color='yellow' size={0.3} position={[0, 0, 0]}>{freeYutText}</Label></Center>}
  </group>
}

export default function HideAndSeekScene() {
  const client = useAtomValue(clientAtom)
  const state = useAtomValue(minigameStateAtom)
  const teams = useAtomValue(teamsAtom)
  const language = useAtomValue(languageAtom)
  const catchEvent = useAtomValue(minigameHideAndSeekCatchAtom)
  const shotEvent = useAtomValue(minigameHideAndSeekShotAtom)
  const [RollStar] = useStarRoll()
  const handledCatchIds = useRef(new Set())
  const handledShotIds = useRef(new Set())
  const { id } = useParams()
  const roomId = id?.toUpperCase()
  const playerId = String(client?._id ?? client?.id ?? '')
  const currentPlayer = state.players?.[playerId]
  const playerTeam = state.players?.[playerId]?.team
  const phase = state.phase ?? 'demo'
  const isHider = playerTeam === state.hiderTeam
  const isSeeker = playerTeam === state.seekerTeam
  const isCaught = Boolean(currentPlayer?.eliminated || currentPlayer?.eliminatedUntil)
  const text = translations.minigames.hideAndSeek
  const labels = translations.minigames.labels
  const boardSpawned = phase === 'demo' || phase === 'playing' || (phase === 'countdown' && state.countdown <= 1)
  const clearedTiles = new Set(state.clearedTiles ?? [])
  const hidersByPlanet = Object.values(state.players ?? {})
    .filter((player) => player.team === state.hiderTeam && Number.isInteger(player.row) && Number.isInteger(player.column))
    .sort((first, second) => first.id.localeCompare(second.id))
    .reduce((groups, player) => {
      const planetIndex = player.row * BOARD_COLUMNS + player.column
      groups[planetIndex] = [...(groups[planetIndex] ?? []), player]
      return groups
    }, {})
  const playersByTeam = [0, 1].map((team) => Object.values(state.players ?? {}).filter((player) => player.team === team))
  const activeHiderCount = playersByTeam[state.hiderTeam]?.filter((player) => !player.eliminated).length ?? 0
  const seekerWinSeconds = Math.max(0, 20 - (state.survivalSeconds ?? 20))
  const [continueSeconds, setContinueSeconds] = useState(10)

  useEffect(() => {
    const { caughtAt, players: caughtPlayers } = catchEvent ?? {}
    if (!caughtAt || !Array.isArray(caughtPlayers) || caughtPlayers.length === 0) return
    const catchId = `${caughtAt}-${caughtPlayers.map((player) => player.id).sort().join('-')}`
    if (handledCatchIds.current.has(catchId)) return
    handledCatchIds.current.add(catchId)
    const [x, z] = cellPosition(caughtPlayers[0].row, caughtPlayers[0].column)
    const color = new THREE.Color().setHSL(caughtPlayers[0].team === 0 ? 0 : 0.5, 0.7, 0.6)
    const starCount = ROLL_STARS_PER_ELIMINATION * caughtPlayers.length
    for (let burst = 0; burst < starCount; burst += 1) {
      const angle = (burst / starCount) * Math.PI * 2
      const speed = 2.5 + (burst % ROLL_STARS_PER_ELIMINATION) * 0.16
      const originOffset = 0.16 + (burst % 3) * 0.04
      RollStar({
        position: new THREE.Vector3(x + Math.cos(angle) * originOffset, 0.5, z + Math.sin(angle) * originOffset),
        rotation: new THREE.Vector3(0, 0, 0),
        omitFactor: 6,
        size: 0.0006,
        scale: 0.5,
        color,
        duration: 0.8,
        speedX: Math.cos(angle) * speed,
        speedY: Math.sin(angle) * speed,
        brightness: 0.3
      })
    }
  }, [catchEvent, RollStar])

  useEffect(() => {
    const { shotAt, team, tileIndex } = shotEvent ?? {}
    if (!shotAt || !Number.isInteger(tileIndex) || (team !== 0 && team !== 1)) return
    const shotId = `${shotAt}-${team}-${tileIndex}`
    if (handledShotIds.current.has(shotId)) return
    handledShotIds.current.add(shotId)
    const [targetX, targetZ] = cellPosition(Math.floor(tileIndex / BOARD_COLUMNS), tileIndex % BOARD_COLUMNS)
    const duration = 0.52
    RollStar({
      position: new THREE.Vector3(targetX, 0.7, targetZ),
      rotation: new THREE.Vector3(0, 0, 0),
      omitFactor: 5,
      size: 0.0012,
      scale: 0.7,
      color: new THREE.Color(team === 0 ? '#ff4d4f' : '#2dd4bf'),
      duration,
      speedX: 0,
      speedY: 0,
      brightness: 0.7
    })
  }, [shotEvent, RollStar])

  useEffect(() => {
    if (phase !== 'finished') {
      setContinueSeconds(10)
      return undefined
    }
    if (continueSeconds === 0) {
      emit('minigameReturnToGame')
      return undefined
    }
    const timeoutId = window.setTimeout(() => setContinueSeconds((seconds) => seconds - 1), 1000)
    return () => window.clearTimeout(timeoutId)
  }, [continueSeconds, phase])

  function emit(event, payload = {}) {
    if (roomId) socket.emit(event, { roomId, ...payload })
  }

  function move(direction) {
    emit('minigameHideAndSeekMove', { direction })
  }

  function jumpToStar(tileIndex) {
    emit('minigameHideAndSeekJump', { tileIndex })
  }

  useEffect(() => {
    const directions = { ArrowLeft: 'left', a: 'left', ArrowRight: 'right', d: 'right', ArrowUp: 'up', w: 'up', ArrowDown: 'down', s: 'down' }
    function moveHider(event) {
      const direction = directions[event.key]
      if (!direction || !isHider) return
      event.preventDefault()
      move(direction)
    }
    window.addEventListener('keydown', moveHider)
    return () => {
      window.removeEventListener('keydown', moveHider)
      document.body.style.cursor = 'default'
    }
  }, [isHider, roomId])

  return <group name='hide-and-seek-minigame'>

    {phase !== 'finished' && <>
      {Array.from({ length: BOARD_ROWS * BOARD_COLUMNS }, (_, index) => {
        return <Planet
          key={index}
          index={index}
          partiallyCleared={(state.tileClickCounts?.[index] ?? 0) === 1}
          cleared={clearedTiles.has(index)}
          spawned={boardSpawned}
          clickable={(phase === 'demo' || phase === 'playing') && (isHider || isSeeker)}
          onTileClick={(tileIndex) => isHider ? jumpToStar(tileIndex) : emit('minigameHideAndSeekTileClick', { tileIndex })}
        />
      })}
      {Object.values(hidersByPlanet).flatMap((hiders) => hiders.map((player, formationSlot) => <HiderToken key={player.id} player={player} formationSlot={formationSlot} formationCount={hiders.length} isCurrentPlayer={player.id === playerId} highHover={isHider} spawned={boardSpawned} />))}

    {(phase === 'demo' && <>
      <Center position={[0, 0.25, -13.45]}>
        <Label color='yellow' size={0.9} position={[0, 0, 0]}>{text.title[language]}</Label>
      </Center>
      <Center position={[0, 0.25, -12.1]}>
        <Label color='white' size={0.34} position={[0, 0, 0]}>{labels.howToPlay[language]}</Label>
      </Center>
    </> )}
    <Center position={[0, 0.25, phase === 'playing' ? -14.45 : -10.75]}>
      <Label color={isHider && isCaught ? 'red' : isHider ? '#73d13d' : '#facc15'} size={0.6} position={[0, 0, 0]}>{isHider ? isCaught ? text.caught[language] : text.hider[language] : isSeeker ? text.seeker[language] : text.spectator[language]}</Label>
    </Center>
    {phase === 'demo' && isHider ? <>
      <Center position={[0, 0.25, 8.1]}><Label color='yellow' size={0.46} position={[0, 0, 0]}>{text.stayOnStar[language]}</Label></Center>
    </> : phase === 'demo' ? <>
      <Center position={[0, 0.25, 8.8]}><Label color='white' size={0.42} position={[0, 0, 0]}>{text.clickStars[language]}</Label></Center>
    </> : null}
    {(phase === 'demo' || phase === 'playing') && isHider && <group position={[4, 0.15, 11.6]}>
      <ControlButton direction='up' position={[0, 0, -1.55]} onClick={() => move('up')} />
      <ControlButton direction='left' position={[-1.55, 0, 0]} onClick={() => move('left')} />
      <ControlButton direction='right' position={[1.55, 0, 0]} onClick={() => move('right')} />
      <ControlButton direction='down' position={[0, 0, 1.55]} onClick={() => move('down')} />
    </group>}
    {phase === 'demo' && <ActionButton position={isHider ? [-4.5, 0.15, 10.4] : [2, 0.15, 10.4]} scale={1.45} boxDims={[2.7, 0.08, 0.72]} onClick={() => { emit('minigameStartGame'); trackEvent({ surface: 'minigame', action: 'minigame_started', category: 'gameplay', metadata: { type: 'hide_and_seek' } }) }}>{translations.minigames.actions.start[language]}</ActionButton>}
    {phase === 'countdown' && <Center position={[0, 3, 1]}><Label color='yellow' size={1.5} position={[0, 0, 0]}>{`${state.countdown}...`}</Label></Center>}
    {phase === 'playing' && <>
      <Label color={state.hiderTeam === 0 ? 'red' : 'turquoise'} size={0.44} position={[-5.6, 0.25, -11.75]}>{`${teams[state.hiderTeam]?.name ?? (state.hiderTeam === 0 ? 'ROCKETS' : 'UFOS')} ${activeHiderCount}`}</Label>
      <Label color='yellow' size={0.65} position={[-0.45, 0.25, -11.8]}>{state.timeRemaining ?? 20}</Label>
      <Label color={state.seekerTeam === 0 ? 'red' : 'turquoise'} size={0.44} position={[2.1, 0.25, -11.75]}>{`${teams[state.seekerTeam]?.name ?? (state.seekerTeam === 0 ? 'ROCKETS' : 'UFOS')} ${playersByTeam[state.seekerTeam]?.length ?? 0}`}</Label>
    </>}
    {phase === 'demo' && <ActionButton position={phase === 'demo' ? (isHider ? [-4.5, 0.15, 12.3] : [-2, 0.15, 10.4]) : [-4.7, 0.15, 10.8]} scale={phase === 'demo' ? 1.45 : 1} boxDims={[2.5, 0.08, 0.72]} onClick={() => { emit('minigameReturnToGame'); trackEvent({ surface: 'minigame', action: 'exit', category: 'ui', metadata: { type: 'hide_and_seek' } }) }}>{translations.minigames.actions.back[language]}</ActionButton>}
    </>}
    {phase === 'finished' && <>
      <Center position={[0, 0.25, -7.2]}><Label color={state.winnerTeam === 0 ? 'red' : 'turquoise'} size={0.72} position={[0, 0, 0]}>{`${teams[state.winnerTeam]?.name ?? (state.winnerTeam === 0 ? labels.rockets[language] : labels.ufos[language])} ${state.finishReason === 'foundAll' ? text.foundAll[language] : text.survived[language]}`}</Label></Center>
      {state.finishReason === 'foundAll' && <Center position={[0, 0.25, -5]}><Label color='white' size={0.42} position={[0, 0, 0]}>{text.tookSeconds[language].replace('{seconds}', seekerWinSeconds)}</Label></Center>}
      <ResultTeamColumn team={0} players={playersByTeam[0]} teamName={teams[0]?.name ?? labels.rockets[language]} winner={state.winnerTeam === 0} freeYutText={labels.freeYut[language]} />
      <ResultTeamColumn team={1} players={playersByTeam[1]} teamName={teams[1]?.name ?? labels.ufos[language]} winner={state.winnerTeam === 1} freeYutText={labels.freeYut[language]} />
      <ActionButton position={[0, 0.15, 6.8]} boxDims={[3.5, 0.08, 0.9]} onClick={() => emit('minigameReturnToGame')}>{`${translations.minigames.actions.continue[language]} ${continueSeconds}`}</ActionButton>
    </>}
  </group>
}
